import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { useCanvasFocus } from '@/src/renderer/contexts/CanvasFocusContext'
import { useTabId } from '@/src/renderer/pages/TabStack/ActiveTabContext'
import type { Project, Task } from '@/src/renderer/lib/db/models'
import { useRestFromDetails } from './useRestFromDetails'
import { wakeTaskUpdate } from '../utils/buildTaskUpdate'
import { onAppEvent } from '@/src/renderer/lib/events'
import { recordRecent } from '@/src/renderer/lib/recent/recentItems'
import type { TaskDetailsOpener } from './TaskDetailsOpener'

/** Wie lange die Karte beim Zumachen im Bild bleibt — so lange wie die Bewegung der Liste. */
export const DETAILS_MOTION_MS = 250

/**
 * Was der Griff von der Seite braucht: **wie ihre Auswahl funktioniert**.
 *
 * Bewusst schmal und nicht `useProjectNavigation`: **Demnächst** führt eine
 * einzelne Kennung statt einer Menge und kennt weder `flatNavList` noch einen
 * Hüllen-Ref. Jede Seite reicht hier durch, was sie hat.
 */
export interface TaskDetailsNavigation {
  /** Die gerade ausgewählten Aufgaben — Abschnittsköpfe dürfen mit darin stehen. */
  selectedTaskIds: ReadonlySet<string>
  /** Wählt diese Zeile; `true`, wenn es sie in der Navigation wirklich gibt. */
  selectTask: (taskId: string) => boolean
  /** Eine Aufgabe weiter — ohne an Abschnittsköpfen zu halten (ADR 0022). */
  stepTask: (direction: 'prev' | 'next') => void
  /** Ob es in diese Richtung überhaupt eine nächste Aufgabe gibt. */
  canStep: (direction: 'prev' | 'next') => boolean
  /** Die Tastatur zurück an die Liste. */
  focusList: () => void
  /** Die oberste SICHTBARE Zeile — für das Öffnen ohne Auswahl. */
  topVisibleTaskId: () => string | null
  /**
   * Die Pfeiltasten stehen auf einem Abschnittskopf. Die Auswahl ist dann LEER — der Kopf trägt
   * nur den Fokus —, und doch ist das nicht „ganz ohne Auswahl“: Dort bleibt die Zeile „Keine
   * Aufgabe ausgewählt“ statt des Projekts (CONTEXT.md, Aufgaben-Details).
   */
  sectionFocused?: boolean
}

export interface UseTaskDetailsPaneParams {
  /** Die Aufgaben der Seite, in Listenreihenfolge. */
  tasks: readonly Task[] | undefined
  navigation: TaskDetailsNavigation
  /**
   * Ob die Seite gerade überhaupt Platz für die Spalte hat. Im **Projekt** ist
   * das false im Listen- und Stapel-Modus und neben dem leeren Zustand; wo es
   * nur eine Liste gibt, bleibt es true.
   */
  allowed?: boolean
  /**
   * **Das Projekt der Seite** (#220). Mit ihm öffnen die Details ohne Auswahl beim Projekt, statt
   * die oberste Zeile zu markieren, und rasten auch ohne Zeile ein. Upcoming und Quick Notes haben
   * kein einzelnes Projekt und geben keins mit.
   */
  project?: Project | null
  setOptimisticTaskOverride: (taskId: string, partial: Partial<Task>) => void
  /**
   * Was die Liste der Seite neben den Details mindestens braucht, falls mehr als eine
   * Aufgabenliste — danach weicht die Navigation (`MainLayout`, #226). Demnächst für die Zeitleiste,
   * die Projektseite für die Buttons im Kopf (#296).
   */
  listMinWidth?: number
}

/**
 * **Die Aufgaben-Details einer Seite** — auf, zu, und wen sie zeigen.
 *
 * Herausgezogen aus `ProjectPage` (Abnahme 21.09.2026, F-KN4/F-DN3): Die Spalte
 * gab es nur im Projekt, gebraucht wird sie auch in **Kurz notiert** und
 * **Demnächst**. Dreimal dieselben zwölf Fäden zu spannen wären drei Orte, die
 * auseinanderlaufen.
 *
 * **Was hier NICHT liegt:** alles, was mit der **Pinnwand** zu tun hat
 * (`detailsOverCanvas`, das Zumachen beim Aufgehen des Canvas). Das gehört dem
 * Projekt allein; die beiden anderen Seiten haben keine Pinnwand. Für den Fall
 * gibt es {@link UseTaskDetailsPane.closeWithoutMotion}.
 *
 * Die Regeln, die er trägt, stehen in CONTEXT.md unter **Aufgaben-Details**:
 * Sie hängen an der Auswahl, sie machen sich nie selbst zu, und ohne sichtbare
 * Zeile rastet der Knopf gar nicht erst ein.
 */
export interface UseTaskDetailsPane {
  /** Ob die Spalte offen ist — daran hängt `aria-pressed` am Knopf. */
  open: boolean
  /** Ob sie im Bild steht: offen ODER noch im Abgang. Daran hängt das Layout. */
  visible: boolean
  /** Die Aufgaben, die sie zeigt (die Auswahl, in Listenreihenfolge). */
  tasks: Task[]
  /**
   * Das Projekt, das sie zeigen — nur, wenn GAR NICHTS ausgewählt ist (#220). Ein Abschnittskopf
   * in der Auswahl behält die Leer-Zeile: Die Pfeiltasten halten dort, und die Karte spränge sonst
   * an jedem Abschnitt aufs Projekt und zurück.
   */
  project: Project | null
  /** Gebuchte **Fokuszeit** — nur bei genau einer Aufgabe, sonst `null`. */
  focusedSeconds: number | null
  toggle: () => void
  /**
   * Öffnet die Spalte für die aktuelle Auswahl, ohne sie zu ändern — der Sprung aus „Favoriten“
   * (#210) wählt erst aus und ruft das danach.
   */
  show: () => void
  /** Der Ausgang: das Kreuz und der Knopf. Mit Abgang. */
  close: () => void
  /** Sofort weg, ohne Abgang — wenn etwas anderes den Platz nimmt. */
  closeWithoutMotion: () => void
  stepTask: (direction: 'prev' | 'next') => void
  canStepPrev: boolean
  canStepNext: boolean
  /**
   * Die Zeile im Wann-Kasten. Sie **schaltet um**: Solange eine Aufgabe wach
   * ist, legt sie genau die schlafen und rückt weiter; ruhen alle, weckt sie.
   */
  onRest: () => void
  /** Ob ALLE gemeinten Aufgaben ruhen — dann heißt die Zeile „Aufwecken …". */
  resting: boolean
  /** Die Tastatur zurück an die Liste (Escape in der Karte). */
  releaseFocusToList: () => void
  /** Zählt hoch, wenn der Caret in den Titel soll (Doppelklick, Eingabetaste). */
  focusTitleSignal: number
  signalFocusTitle: () => void
  /** Welche Unteraufgabe die Details zeigen sollen — gesetzt vom Pfeil in der aufgeklappten Karte. */
  subtaskRequest: SubtaskRequest | null
  /** Die Details auf „Kommentare" schalten — gesetzt von der Marke an einer Zeile (#183). */
  commentsRequest: CommentsRequest | null
  /** Die Details mit offenem Chip „Erinnerung“ öffnen — gesetzt von der Glocke an einer Zeile. */
  remindersRequest: RemindersRequest | null
  /** Für `TaskDetailsOpenerProvider`; `null`, solange die Seite keinen Platz für die Spalte hat. */
  opener: TaskDetailsOpener | null
}

/** `n` zählt hoch, damit derselbe Pfeil ein zweites Mal wirkt. */
export interface SubtaskRequest { parentId: string; childId: string; n: number }

/** Mit `parentId` ist `taskId` eine Unteraufgabe dieser Aufgabe. `n` wie bei {@link SubtaskRequest}. */
export interface CommentsRequest { taskId: string; parentId?: string; n: number }

/** Wie {@link CommentsRequest}; `tab: 'at'` öffnet den Chip gleich auf „Datum & Uhrzeit“. */
export interface RemindersRequest extends CommentsRequest { tab?: 'at' }

export function useTaskDetailsPane({
  tasks,
  navigation,
  allowed = true,
  project = null,
  setOptimisticTaskOverride,
  listMinWidth,
}: UseTaskDetailsPaneParams): UseTaskDetailsPane {
  const dataSource = useDataSource()
  const [requested, setRequested] = useState(false)
  const [focusTitleSignal, setFocusTitleSignal] = useState(0)

  const { selectedTaskIds, selectTask, stepTask, canStep, focusList, topVisibleTaskId } = navigation

  /**
   * Was in den Aufgaben-Details steht: die ausgewählten Aufgaben in
   * Listenreihenfolge. Sie hängen an der AUSWAHL, nicht an einer Aufgabe — wer
   * mit den Pfeiltasten durch die Liste läuft, sieht die Eigenschaften
   * mitlaufen.
   */
  const detailsTasks = useMemo(
    () => (tasks ?? []).filter(task => selectedTaskIds.has(task.id)),
    [tasks, selectedTaskIds],
  )

  /*
    Die **Fokuszeit** nur bei EINER ausgewählten Aufgabe: Bei mehreren zeigen
    die Details ohnehin nur, was alle tragen, und eine Summe wäre eine andere
    Aussage.
  */
  const focusIds = useMemo(
    () => (detailsTasks.length === 1 ? [detailsTasks[0]!.id] : []),
    [detailsTasks],
  )
  const focusSecondsMap = dataSource.useFocusSeconds(focusIds)
  const focusedSeconds = detailsTasks.length === 1
    ? focusSecondsMap?.get(detailsTasks[0]!.id) ?? null
    : null

  const open = allowed && requested

  // **Zuletzt geöffnet** (#233): eine Aufgabe, sobald die Details für genau sie aufgehen.
  const recentTaskId = open && detailsTasks.length === 1 ? detailsTasks[0]!.id : null
  useEffect(() => {
    if (recentTaskId) recordRecent({ kind: 'task', id: recentTaskId })
  }, [recentTaskId])

  /*
    **Der Rückweg dauert so lange wie der Hinweg.** Beim Zumachen bleibt die
    Karte eine Viertelsekunde stehen, während die Listenspalte zurückwächst und
    sie nach rechts aus dem Bild schiebt. Ohne diese Wartezeit wäre sie im
    selben Bild weg, in dem die Liste losläuft.

    **Nur beim ZUMACHEN.** Fällt ihre Grundlage weg oder nimmt etwas anderes
    ihren Platz, geht sie sofort — dort läuft schon eine andere Bewegung.
  */
  const [closing, setClosing] = useState(false)
  const skipMotion = useRef(false)
  const wasRequested = useRef(requested)
  useEffect(() => {
    const was = wasRequested.current
    wasRequested.current = requested
    if (requested) {
      setClosing(false)
      return
    }
    if (!was) return
    if (skipMotion.current) {
      skipMotion.current = false
      return
    }
    setClosing(true)
    const timer = window.setTimeout(() => setClosing(false), DETAILS_MOTION_MS)
    return () => window.clearTimeout(timer)
  }, [requested])

  const visible = open || (closing && allowed)

  /*
    **Dem Layout melden, dass rechts die halbe Fläche belegt ist.** Daran hängt
    eine Sache, und ohne sie sitzt die Karte falsch: Die **Breadcrumb** verlässt
    den Fluss und macht ihr die Oberkante frei (`MainLayout.scss`) — dieselbe
    Regel, die schon für den geteilten Canvas gilt. Die **Liste** gleicht die
    entfallene Zeile mit einem Rand aus (`ProjectPage.scss`), die Karte nicht:
    Sie soll ganz oben beginnen.

    Blieb die Meldung aus, stand die Zeile weiter im Fluss und schob die Karte
    um ihre Höhe nach unten (gemeldet 21.09.2026, gleich nachdem Kurz notiert
    und Demnächst die Spalte bekamen).

    Über den Kontext und nicht über Props: Die Zeile liegt in `MainLayout`, also
    oberhalb jeder Seite. Pro Tab abgelegt, damit ein inaktives Tab dem aktiven
    nichts überschreibt.
  */
  const { setDetailsOpen: reportDetailsOpen, clearDetailsOpen } = useCanvasFocus()
  const tabId = useTabId()
  useLayoutEffect(() => {
    if (!visible) return
    reportDetailsOpen(tabId, true, listMinWidth)
    return () => clearDetailsOpen(tabId)
  }, [visible, tabId, reportDetailsOpen, clearDetailsOpen, listMinWidth])

  const releaseFocusToList = useCallback(() => focusList(), [focusList])

  /**
   * Auf und zu. Beim Öffnen fallen zwei Entscheidungen, und beide fallen GENAU
   * HIER — nicht bei jedem Bild:
   *
   * 1. **Welche Aufgabe.** Ohne Auswahl die oberste SICHTBARE Zeile
   *    (CONTEXT.md): Wer weit unten steht, meint das, was dort liegt.
   * 2. **Ob überhaupt.** Findet sich keine, passiert NICHTS — sonst rastete der
   *    Knopf ein und stünde neben einer leeren Stelle.
   *
   * **Mit Projekt fallen beide weg** (#220): Ohne Auswahl zeigen die Details
   * das Projekt, markiert wird nichts.
   *
   * Danach geht die Tastatur an die Liste, aber nur, wenn wirklich etwas
   * geschehen ist.
   */
  const detailsTasksRef = useRef(detailsTasks)
  detailsTasksRef.current = detailsTasks
  const hasProject = project != null
  const toggle = useCallback(() => {
    if (requested) {
      setRequested(false)
      releaseFocusToList()
      return
    }

    // Nicht die Größe der Auswahl: Darin können Abschnittsköpfe stehen, und die
    // zeigen die Aufgaben-Details nicht.
    let willShow = detailsTasksRef.current.length > 0 || hasProject
    if (!willShow) {
      const id = topVisibleTaskId()
      willShow = !!id && selectTask(id)
    }
    if (!willShow) return

    setRequested(true)
    releaseFocusToList()
  }, [requested, releaseFocusToList, selectTask, topVisibleTaskId, hasProject])

  // Ohne Prüfung der Auswahl: Sie wird im selben Zug gesetzt und steht hier noch nicht.
  const show = useCallback(() => {
    setRequested(true)
    releaseFocusToList()
  }, [releaseFocusToList])

  const close = useCallback(() => {
    setRequested(false)
    releaseFocusToList()
  }, [releaseFocusToList])

  /*
    **Der Pfeil an einer Unteraufgabe in der aufgeklappten Karte** (T14 #179): Er wählt ihre
    Aufgabe, öffnet die Details und lässt sie die Unteraufgabe zeigen. Nur die Seite des
    Tabs, aus dem er kam — ein zweites Tab mit derselben Aufgabe bleibt, wie es ist.
  */
  const [subtaskRequest, setSubtaskRequest] = useState<SubtaskRequest | null>(null)
  useEffect(() => onAppEvent('open-subtask', ({ tabId: from, parentId, childId }) => {
    if (!allowed || from !== tabId) return
    if (!selectTask(parentId)) return
    setRequested(true)
    setSubtaskRequest(prev => ({ parentId, childId, n: (prev?.n ?? 0) + 1 }))
  }), [allowed, tabId, selectTask])

  /*
    **Die Marke an einer Zeile** (#183): wählt die Aufgabe (bei einer Unteraufgabe deren Aufgabe),
    öffnet die Details und schaltet sie auf „Kommentare". Die Tastatur geht danach an die Liste —
    wie beim Knopf, der die Details öffnet.
  */
  /*
    **Der Öffner hängt an keinem Griff der Seite** (09.10.2026): Jede Zeile liest ihn aus dem
    Kontext. Demnächst reicht `selectTask` und `focusList` inline herein; wechselte der Öffner mit,
    baute jeder Pfeiltasten-Schritt alle Zeilen der Seite neu (gemessen ~140ms je Schritt). Die
    Griffe kommen deshalb frisch aus Refs.
  */
  const selectTaskRef = useRef(selectTask)
  selectTaskRef.current = selectTask
  const focusListRef = useRef(focusList)
  focusListRef.current = focusList
  const [commentsRequest, setCommentsRequest] = useState<CommentsRequest | null>(null)
  const openComments = useCallback((taskId: string, parentId?: string) => {
    if (!selectTaskRef.current(parentId ?? taskId)) return
    setRequested(true)
    setCommentsRequest(prev => ({ taskId, ...(parentId ? { parentId } : {}), n: (prev?.n ?? 0) + 1 }))
    focusListRef.current()
  }, [])
  // Die Glocke an einer Zeile (Plan task-reminders): derselbe Weg, nur mit offenem Chip „Erinnerung“.
  const [remindersRequest, setRemindersRequest] = useState<RemindersRequest | null>(null)
  const openReminders = useCallback((taskId: string, parentId?: string, tab?: 'at') => {
    if (!selectTaskRef.current(parentId ?? taskId)) return
    setRequested(true)
    setRemindersRequest(prev => ({ taskId, ...(parentId ? { parentId } : {}), ...(tab ? { tab } : {}), n: (prev?.n ?? 0) + 1 }))
    focusListRef.current()
  }, [])
  const opener = useMemo<TaskDetailsOpener | null>(
    () => (allowed ? { openComments, openReminders } : null),
    [allowed, openComments, openReminders],
  )

  /*
    **Eine Anfrage gilt für EIN Öffnen** (gemeldet 29.09.2026). Blieb sie stehen, las die nächste
    Karte sie beim Aufbau erneut: Nach einem Klick auf die Marke öffnete der Knopf die Details
    danach immer auf „Kommentare", nach dem Pfeil an einer Unteraufgabe immer auf ihr. Neu
    geöffnet zeigen die Details die ausgewählte Aufgabe auf dem zuletzt benutzten Reiter.
  */
  useEffect(() => {
    if (requested) return
    setCommentsRequest(null)
    setRemindersRequest(null)
    setSubtaskRequest(null)
  }, [requested])

  /**
   * **Nur, wenn wirklich eine Karte offen stand.** Sonst ändert
   * `setRequested(false)` nichts, der Effekt läuft nicht an, und der Merker
   * bliebe stehen — er schluckte dann den Abgang des NÄCHSTEN Zumachens, lange
   * nachdem die Pinnwand wieder zu ist (gemeldet 2026-09-13, hier beim
   * Herausziehen prompt wiederholt und vom alten Test gefangen).
   */
  const requestedRef = useRef(requested)
  requestedRef.current = requested
  const closeWithoutMotion = useCallback(() => {
    if (!requestedRef.current) return
    skipMotion.current = true
    setRequested(false)
  }, [])

  /**
   * **Das Pfeil-Paar im Kopf.** Es verschiebt die AUSWAHL — dieselbe Wahrheit,
   * die auch die Pfeiltasten bewegen —, hält aber nicht am Abschnittskopf an.
   * Danach gehört die Tastatur wieder der Liste: Sonst fräße der Knopf die
   * nächste Pfeiltaste.
   */
  const handleStepTask = useCallback((direction: 'prev' | 'next') => {
    stepTask(direction)
    releaseFocusToList()
  }, [stepTask, releaseFocusToList])

  /*
    Bei MEHREREN ausgewählten Aufgaben sind beide Pfeile aus — es gibt kein „die
    nächste" für drei Zeilen. Auf einem Abschnittskopf (keine Aufgabe
    ausgewählt) bleiben sie scharf: Dort sind sie der einzige Mausweg aus dem
    Leerzustand.
  */
  const canStepBoth = detailsTasks.length <= 1
  const canStepPrev = canStepBoth && canStep('prev')
  const canStepNext = canStepBoth && canStep('next')

  const restFromDetails = useRestFromDetails({
    navList: useMemo(
      () => (tasks ?? []).map(task => ({ type: 'task' as const, id: task.id })),
      [tasks],
    ),
    selectTask,
    setOptimisticTaskOverride,
  })
  /**
   * **Die Zeile im Wann-Kasten schaltet um** (Abnahme 21.09.2026, F-AD1): Sie
   * bot „Ruhen lassen" auch an einer Aufgabe an, die längst ruhte.
   *
   * Die Misch-Regel ist dieselbe wie im **Aufgaben-Menü** (CONTEXT.md): Solange
   * eine wach ist, legt die Zeile genau die schlafen; erst wenn alle ruhen,
   * weckt sie. Das Wecken holt dabei den Termin nach vorn
   * ({@link wakeTaskUpdate}) — eine wiederkehrende Aufgabe, die über ihr Datum
   * hinweg geschlafen hat, wacht am nächsten auf statt überfällig.
   */
  const resting = detailsTasks.length > 0 && detailsTasks.every(task => task.pausedAt != null)
  const onRest = useCallback(() => {
    const list = detailsTasksRef.current
    if (list.length > 0 && list.every(task => task.pausedAt != null)) {
      const now = new Date()
      for (const task of list) setOptimisticTaskOverride(task.id, wakeTaskUpdate(task, now))
      void dataSource
        .updateTasks(list.map(task => ({ id: task.id, changes: wakeTaskUpdate(task, now) })))
        .catch(err => console.error('[TaskDetails] Failed to wake tasks:', err))
    } else {
      void restFromDetails(list)
    }
    releaseFocusToList()
  }, [restFromDetails, releaseFocusToList, setOptimisticTaskOverride, dataSource])

  const signalFocusTitle = useCallback(() => setFocusTitleSignal(n => n + 1), [])

  return {
    subtaskRequest,
    commentsRequest,
    remindersRequest,
    opener,
    open,
    visible,
    tasks: detailsTasks,
    project: selectedTaskIds.size === 0 && !navigation.sectionFocused ? project : null,
    focusedSeconds,
    toggle,
    show,
    close,
    closeWithoutMotion,
    stepTask: handleStepTask,
    canStepPrev,
    canStepNext,
    onRest,
    resting,
    releaseFocusToList,
    focusTitleSignal,
    signalFocusTitle,
  }
}
